import React, {
  useCallback, useEffect, useMemo,
  useState, } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,   Box, GoPaperlessIcon, Preloader,
  Text, useResponsive, } from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/wrappers';
import { ElectronicAgreement } from '@/modules/transaction/components/Statements/components/ElectronicAgreement';
import { Steps } from '@/modules/transaction/components/Statements/components/GoPaperless/GoPaperless.constants';
import useUpdateEmployeeQuery from '@/modules/user/queries/useUpdateEmployee.query';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';
import globalTheme from '@/styles/theme';

interface Props {
  onSetVisible: (value: boolean) => void;
  setStep: (value: Steps) => void;
  onExternalSubmit?: () => Promise<void> | void;
  isCheckboxOptional?: boolean;
  onBack?: () => void;
}

const GoPaperlessModal: React.FC<Props> = ({
  onSetVisible, setStep, onExternalSubmit, isCheckboxOptional, onBack,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { employee } = useEmployeeStore();
  const [isChecked, setIsChecked] = useState(false);
  const [isAgreementError, setIsAgreementError] = useState(false);
  const { updateEmployee, isLoading, isSuccess } = useUpdateEmployeeQuery();

  const onCancel = useCallback(() => onSetVisible(false), [onSetVisible]);

  const currentEmail = useMemo(
    () => (employee?.isEmployeeEmailPreferred && employee.employeeProvidedEmail
      ? employee.employeeProvidedEmail
      : employee?.employerProvidedEmail
    ), [employee],
  );

  const handleChangeEmail = useCallback(() => {
    setStep(Steps.ChangeEmail);
  }, [setStep]);

  const handleCheck = useCallback(() => {
    setIsChecked(!isChecked);
  }, [setIsChecked, isChecked]);

  const handleSubmit = useCallback(async () => {
    if (!isCheckboxOptional && !isChecked) {
      setIsAgreementError(true);
      return;
    }

    if (onExternalSubmit) {
      await onExternalSubmit();
      if (isChecked) {
        updateEmployee({
          is_electronic_statement_only: true,
        });
      }
      return;
    }

    updateEmployee({
      is_electronic_statement_only: true,
    });
  }, [updateEmployee, isChecked, onExternalSubmit, isCheckboxOptional]);

  useEffect(() => {
    if (isSuccess) {
      setStep(Steps.Success);
    }
  }, [isSuccess, setStep]);

  return (
    <ModalWrapper
      visible
      onSetVisible={onSetVisible}
      contentStyle={{
        padding: isMobile
          ? `${globalTheme.spacings.spacing24} ${globalTheme.spacings.spacing12}`
          : `${globalTheme.spacings.spacing48} ${globalTheme.spacings.spacing24}`,
      }}
      testId="go-paperless"
    >
      <Box align="center">
        <GoPaperlessIcon width={isMobile ? '128px' : '120px'} height={isMobile ? '128px' : '120px'} />
        {!isMobile && (
          <Box margin={{ top: 'spacing12' }}>
            <Text size="2xl" weight={700}>
              {t('Go Paperless')}
            </Text>
          </Box>
        )}
      </Box>

      <Box
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        round={isMobile ? 'container1Round' : 'moduleRound'}
        background="module"
        pad={isMobile ? 'spacing8' : 'spacing24'}
      >
        <ElectronicAgreement
          isChecked={isChecked}
          onChecked={handleCheck}
          onEdit={handleChangeEmail}
          email={currentEmail}
          isError={isAgreementError && !isChecked}
          wrapperMode
          isMobileLayout={isMobile}
        />
      </Box>

      {
        isAgreementError && !isChecked && (
          <Box margin={{ top: 'spacing24' }} data-testId="error_block">
            <Text color="danger" textAlign="end">
              {t('Please confirm that you’ve read and acknowledge our Electronic Disclosure Agreement.')}
            </Text>
          </Box>
        )
      }

      <Box
        margin={{ top: isMobile ? 'spacing16' : 'spacing24' }}
        direction={isMobile ? 'column' : 'row'}
        justify="end"
        gap="spacing12"
        pad={isMobile ? { horizontal: 'spacing4' } : undefined}
      >
        <AppButton
          buttonType="secondary"
          width={isMobile ? '100%' : '160px'}
          onClick={onBack || onCancel}
          testId={onBack ? 'back' : 'cancel'}
        >
          {onBack ? t('Back') : t('Cancel')}
        </AppButton>
        <AppButton
          width={isMobile ? '100%' : '160px'}
          onClick={handleSubmit}
          testId="submit"
          disabled={!currentEmail}
        >
          {isLoading ? <Preloader /> : t('Submit')}
        </AppButton>
      </Box>
    </ModalWrapper>
  );
};

export default GoPaperlessModal;
